import { useDestinations } from '@/hooks/useDestinations';
import { useTranslation } from 'react-i18next';

export default function DestinationsGrid() {
  const { destinations, loading, error, refresh } = useDestinations();
  const { t } = useTranslation();

  return (
    <section className="w-full bg-background-200 py-8 md:py-12 lg:py-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col gap-5">
          <h2 className="text-[18px] font-bold leading-[1.2] tracking-tight text-foreground-950 lg:text-[30px]">
            {t('destinations.title')}
          </h2>

          {loading ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="flex h-full flex-col overflow-hidden rounded-xl border border-background-200/70 bg-white"
                >
                  <div className="aspect-[4/3] w-full animate-pulse bg-background-100" />
                  <div className="flex flex-1 flex-col gap-2 p-3.5">
                    <div className="h-4 w-2/3 animate-pulse rounded bg-background-100" />
                    <div className="h-3 w-full animate-pulse rounded bg-background-100" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-background-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-4 rounded-xl border border-background-200/70 bg-white py-12 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-map-pin-line text-secondary-400 text-xl" />
              </div>
              <p className="text-sm text-secondary-500">{t('destinations.error')}</p>
              <button
                type="button"
                onClick={refresh}
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                {t('destinations.tryAgain')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
              {destinations.map((dest) => (
                <a
                  key={dest.id}
                  href={`/tours?destination=${dest.id}`}
                  aria-label={dest.name}
                  className="group flex h-full flex-col overflow-hidden rounded-xl border border-background-200/70 bg-white shadow-[0_4px_14px_-6px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_18px_36px_-18px_rgba(16,185,129,0.35)]"
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden">
                    <img
                      src={dest.image}
                      alt={dest.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-1.5 p-3.5">
                    <h3 className="text-[16px] font-bold leading-[1.2] text-foreground-950 lg:text-[18px]">
                      {dest.name}
                    </h3>
                    <p className="line-clamp-2 text-[12px] leading-[1.5] text-secondary-500">
                      {dest.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}